export const DEVICE_MESSAGE_VERSION = 1;

export const DEVICE_MESSAGE_TYPES = Object.freeze({
  PERIPHERAL_CONTROL: "peripheral-control",
});

export const DEVICE_MESSAGE_ACTION = "message";
export const MAIN_CAMERA_RESOURCE = "main-camera";
export const CLINICAL_DEVICES_RESOURCE = "clinical-devices";

export const CLINICAL_DEVICE_MESSAGE_TASKS = Object.freeze({
  STATUS_REQUEST: "status-request",
  STATUS: "status",
  STREAM_START: "stream-start",
  STREAM_STOP: "stream-stop",
  STREAM_STATUS: "stream-status",
});

export const CLINICAL_STREAM_STATES = Object.freeze({
  STARTING: "starting",
  READY: "ready",
  CONNECTING: "connecting",
  STREAMING: "streaming",
  RECONNECTING: "reconnecting",
  STOPPING: "stopping",
  STOPPED: "stopped",
  FAILED: "failed",
});

export const CLINICAL_DEVICE_IDS = Object.freeze({
  STETHOSCOPE: "stethoscope",
  EAR_CAMERA: "ear-camera",
  EXAM_CAMERA: "exam-camera",
});

export const PTZ_MESSAGE_TASKS = Object.freeze({
  MOVE: "move",
  STATUS_REQUEST: "status-request",
  STATUS: "status",
  RESULT: "result",
});

export const PTZ_MOVEMENTS = Object.freeze({
  LEFT: "left",
  RIGHT: "right",
  UP: "up",
  DOWN: "down",
  ZOOM_IN: "zoom-in",
  ZOOM_OUT: "zoom-out",
});

export const PTZ_RESULT_STATUSES = Object.freeze({
  APPLIED: "applied",
  NOT_READY: "not-ready",
  UNSUPPORTED: "unsupported",
  REJECTED: "rejected",
  FAILED: "failed",
});

export const CAMERA_CONTROL_STATES = Object.freeze({
  UNAVAILABLE: "unavailable",
  READY: "ready",
  STARTING: "starting",
  STREAMING: "streaming",
  RECONNECTING: "reconnecting",
  STOPPING: "stopping",
  ERROR: "error",
});

export const DEFAULT_PTZ_STEP = 5;
export const MIN_PTZ_STEP = 1;
export const MAX_PTZ_STEP = 10;

export const DEFAULT_DEVICE_MESSAGE_TYPES = Object.freeze(
  Object.values(DEVICE_MESSAGE_TYPES),
);

export const MAX_DEVICE_MESSAGE_LENGTH = 64 * 1024;

const MAX_IDENTIFIER_LENGTH = 256;
const MAX_MESSAGE_LENGTH = 512;
const PTZ_TASK_VALUES = Object.values(PTZ_MESSAGE_TASKS);
const PTZ_MOVEMENT_VALUES = Object.values(PTZ_MOVEMENTS);
const PTZ_RESULT_VALUES = Object.values(PTZ_RESULT_STATUSES);
const CAMERA_STATE_VALUES = Object.values(CAMERA_CONTROL_STATES);
const CLINICAL_DEVICE_TASK_VALUES = Object.values(CLINICAL_DEVICE_MESSAGE_TASKS);
const CLINICAL_DEVICE_ID_VALUES = Object.values(CLINICAL_DEVICE_IDS);
const CLINICAL_STREAM_STATE_VALUES = Object.values(CLINICAL_STREAM_STATES);

export class DeviceMessageProtocolError extends Error {
  constructor(message) {
    super(message);
    this.name = "DeviceMessageProtocolError";
  }
}

const assertString = (value, fieldName, { maxLength = MAX_IDENTIFIER_LENGTH } = {}) => {
  if (typeof value !== "string" || value.length === 0 || value.length > maxLength) {
    throw new DeviceMessageProtocolError(`Device message ${fieldName} is invalid`);
  }
};

const assertVisitContext = (message, { encounterId, organizationId } = {}) => {
  assertString(message.encounterId, "encounterId");
  assertString(message.organizationId, "organizationId");

  if (encounterId && message.encounterId !== encounterId) {
    throw new DeviceMessageProtocolError("Device message encounter does not match the active visit");
  }

  if (organizationId && message.organizationId !== organizationId) {
    throw new DeviceMessageProtocolError("Device message organization does not match the active visit");
  }
};

const assertCapabilities = (capabilities) => {
  if (!capabilities || typeof capabilities !== "object" || Array.isArray(capabilities)) {
    throw new DeviceMessageProtocolError("Device message capabilities are invalid");
  }

  ["pan", "tilt", "zoom"].forEach((capability) => {
    if (typeof capabilities[capability] !== "boolean") {
      throw new DeviceMessageProtocolError(`Device message ${capability} capability is invalid`);
    }
  });
};

const assertMainCameraMessage = (message, context) => {
  if (message.version !== DEVICE_MESSAGE_VERSION) {
    throw new DeviceMessageProtocolError("Unsupported device message version");
  }

  if (message.action !== DEVICE_MESSAGE_ACTION) {
    throw new DeviceMessageProtocolError("Unsupported device message action");
  }

  if (message.resource !== MAIN_CAMERA_RESOURCE) {
    throw new DeviceMessageProtocolError("Unsupported camera resource");
  }

  if (!PTZ_TASK_VALUES.includes(message.task)) {
    throw new DeviceMessageProtocolError("Unsupported camera-control task");
  }

  assertVisitContext(message, context);

  if (message.task === PTZ_MESSAGE_TASKS.STATUS_REQUEST) {
    assertString(message.requestId, "requestId");
    return;
  }

  assertString(message.sessionInstanceId, "sessionInstanceId");
  if (!Number.isSafeInteger(message.cameraGeneration) || message.cameraGeneration < 0) {
    throw new DeviceMessageProtocolError("Device message cameraGeneration is invalid");
  }

  if (message.task === PTZ_MESSAGE_TASKS.MOVE) {
    assertString(message.commandId, "commandId");
    if (!PTZ_MOVEMENT_VALUES.includes(message.movement)) {
      throw new DeviceMessageProtocolError("Unsupported PTZ movement");
    }
    if (
      !Number.isInteger(message.step)
      || message.step < MIN_PTZ_STEP
      || message.step > MAX_PTZ_STEP
    ) {
      throw new DeviceMessageProtocolError("PTZ step is outside the supported range");
    }
    return;
  }

  if (message.task === PTZ_MESSAGE_TASKS.STATUS) {
    if (!CAMERA_STATE_VALUES.includes(message.state)) {
      throw new DeviceMessageProtocolError("Unsupported camera-control state");
    }
    ["available", "streaming", "ptzReady"].forEach((fieldName) => {
      if (typeof message[fieldName] !== "boolean") {
        throw new DeviceMessageProtocolError(`Device message ${fieldName} is invalid`);
      }
    });
    assertCapabilities(message.capabilities);
    if (message.message !== undefined) {
      assertString(message.message, "message", { maxLength: MAX_MESSAGE_LENGTH });
    }
    return;
  }

  assertString(message.commandId, "commandId");
  if (!PTZ_RESULT_VALUES.includes(message.status)) {
    throw new DeviceMessageProtocolError("Unsupported PTZ result status");
  }
  if (message.message !== undefined) {
    assertString(message.message, "message", { maxLength: MAX_MESSAGE_LENGTH });
  }
};

const assertClinicalDeviceMessage = (message, context) => {
  if (message.version !== DEVICE_MESSAGE_VERSION) {
    throw new DeviceMessageProtocolError("Unsupported device message version");
  }

  if (message.action !== DEVICE_MESSAGE_ACTION) {
    throw new DeviceMessageProtocolError("Unsupported device message action");
  }

  if (message.resource !== CLINICAL_DEVICES_RESOURCE) {
    throw new DeviceMessageProtocolError("Unsupported clinical-device resource");
  }

  if (!CLINICAL_DEVICE_TASK_VALUES.includes(message.task)) {
    throw new DeviceMessageProtocolError("Unsupported clinical-device task");
  }

  assertVisitContext(message, context);

  if (message.task === CLINICAL_DEVICE_MESSAGE_TASKS.STATUS_REQUEST) {
    assertString(message.requestId, "requestId");
    return;
  }

  if (message.task === CLINICAL_DEVICE_MESSAGE_TASKS.STREAM_START) {
    assertString(message.requestId, "requestId");
    assertString(message.streamSessionId, "streamSessionId");
    if (!CLINICAL_DEVICE_ID_VALUES.includes(message.deviceId)) {
      throw new DeviceMessageProtocolError("Clinical stream device is invalid");
    }
    return;
  }

  if (message.task === CLINICAL_DEVICE_MESSAGE_TASKS.STREAM_STOP) {
    assertString(message.requestId, "requestId");
    assertString(message.streamSessionId, "streamSessionId");
    if (!CLINICAL_DEVICE_ID_VALUES.includes(message.deviceId)) {
      throw new DeviceMessageProtocolError("Clinical stream device is invalid");
    }
    return;
  }

  if (message.task === CLINICAL_DEVICE_MESSAGE_TASKS.STREAM_STATUS) {
    assertString(message.requestId, "requestId");
    assertString(message.streamSessionId, "streamSessionId");
    assertString(message.sessionInstanceId, "sessionInstanceId");
    if (!CLINICAL_DEVICE_ID_VALUES.includes(message.deviceId)) {
      throw new DeviceMessageProtocolError("Clinical stream device is invalid");
    }
    if (!CLINICAL_STREAM_STATE_VALUES.includes(message.state)) {
      throw new DeviceMessageProtocolError("Clinical stream state is invalid");
    }
    if (!Number.isSafeInteger(message.sequence) || message.sequence < 1) {
      throw new DeviceMessageProtocolError("Clinical stream sequence is invalid");
    }
    if (message.errorCode !== undefined) assertString(message.errorCode, "errorCode");
    if (message.message !== undefined) {
      assertString(message.message, "message", { maxLength: MAX_MESSAGE_LENGTH });
    }
    assertString(message.updatedAt, "updatedAt");
    if (Number.isNaN(Date.parse(message.updatedAt))) {
      throw new DeviceMessageProtocolError("Clinical stream timestamp is invalid");
    }
    return;
  }

  assertString(message.sessionInstanceId, "sessionInstanceId");
  if (!Number.isSafeInteger(message.revision) || message.revision < 0) {
    throw new DeviceMessageProtocolError("Device message revision is invalid");
  }

  if (!Array.isArray(message.devices) || message.devices.length !== CLINICAL_DEVICE_ID_VALUES.length) {
    throw new DeviceMessageProtocolError("Clinical-device snapshot is incomplete");
  }

  const receivedDeviceIds = new Set();
  message.devices.forEach((device) => {
    if (!device || typeof device !== "object" || Array.isArray(device)) {
      throw new DeviceMessageProtocolError("Clinical-device status is invalid");
    }
    if (!CLINICAL_DEVICE_ID_VALUES.includes(device.id) || receivedDeviceIds.has(device.id)) {
      throw new DeviceMessageProtocolError("Clinical-device id is invalid");
    }
    if (typeof device.available !== "boolean") {
      throw new DeviceMessageProtocolError("Clinical-device availability is invalid");
    }
    receivedDeviceIds.add(device.id);
  });

  assertString(message.updatedAt, "updatedAt");
  if (Number.isNaN(Date.parse(message.updatedAt))) {
    throw new DeviceMessageProtocolError("Clinical-device timestamp is invalid");
  }
};

const assertSupportedMessage = (
  message,
  {
    encounterId,
    organizationId,
    allowedTypes = DEFAULT_DEVICE_MESSAGE_TYPES,
  } = {},
) => {
  if (!message || typeof message !== "object" || Array.isArray(message)) {
    throw new DeviceMessageProtocolError("Device message must be a JSON object");
  }

  if (typeof message.type !== "string" || !allowedTypes.includes(message.type)) {
    throw new DeviceMessageProtocolError("Unsupported device message type");
  }

  const context = { encounterId, organizationId };
  if (message.resource === MAIN_CAMERA_RESOURCE) {
    assertMainCameraMessage(message, context);
  } else if (message.resource === CLINICAL_DEVICES_RESOURCE) {
    assertClinicalDeviceMessage(message, context);
  } else {
    if (message.encounterId && encounterId && message.encounterId !== encounterId) {
      throw new DeviceMessageProtocolError("Device message encounter does not match the active visit");
    }

    if (message.organizationId && organizationId && message.organizationId !== organizationId) {
      throw new DeviceMessageProtocolError("Device message organization does not match the active visit");
    }
  }

  return message;
};

export const parseDeviceMessage = (rawData, context = {}) => {
  if (typeof rawData !== "string") {
    throw new DeviceMessageProtocolError("Binary device messages are not supported");
  }

  if (rawData.length > MAX_DEVICE_MESSAGE_LENGTH) {
    throw new DeviceMessageProtocolError("Device message exceeds the size limit");
  }

  let message;
  try {
    message = JSON.parse(rawData);
  } catch {
    throw new DeviceMessageProtocolError("Device message is not valid JSON");
  }

  return assertSupportedMessage(message, context);
};

export const serializeDeviceMessage = (message, context = {}) => {
  const validatedMessage = assertSupportedMessage(message, context);
  const serializedMessage = JSON.stringify(validatedMessage);

  if (serializedMessage.length > MAX_DEVICE_MESSAGE_LENGTH) {
    throw new DeviceMessageProtocolError("Device message exceeds the size limit");
  }

  return serializedMessage;
};

export const createDeviceMessageId = (prefix = "device") => {
  const browserCrypto = typeof window !== "undefined" ? window.crypto : null;
  const randomId = browserCrypto?.randomUUID?.()
    || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${randomId}`;
};

export const createClinicalViewerClientId = (streamSessionId) => (
  `clinical-${String(streamSessionId || "")}`
    .replace(/[^A-Za-z0-9_-]/g, "-")
    .slice(0, 256)
);

export const normalizeScopeOfPractice = (scopeOfPractice) => {
  if (Array.isArray(scopeOfPractice)) {
    return scopeOfPractice.filter((scope) => typeof scope === "string" && scope.length > 0);
  }

  return typeof scopeOfPractice === "string" && scopeOfPractice.length > 0
    ? [scopeOfPractice]
    : [];
};
